import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import InfiniteScroll from 'react-infinite-scroll-component'

import { useSession } from '@/modules/access/hooks/useSession'
import { useGetUserOrganisations } from '@/modules/access/pages/user/hooks/useGetUserOrganisations'
import { Spinner } from '@/modules/requisitions/components/spinner/Spinner'
import { Input } from '@/modules/shared/components/input/Input'
import { QueryResult } from '@/modules/shared/components/query-result/QueryResult'
import { useDebounce } from '@/modules/shared/hooks/useDebounce'
import SelectOrganisationUserListItem from '@/modules/shared/layouts/header/switch-organisation-modal/SelectOrganisationUserListItem'

interface SelectOrganisationUserListProps {
  onSelectOrganisation: Dispatch<SetStateAction<number | null>>
  selectedOrganisation: number | null
  showModal?: boolean
}

export default function SelectOrganisationUserList({
  onSelectOrganisation,
  selectedOrganisation,
  showModal,
}: SelectOrganisationUserListProps) {
  const { t } = useTranslation()
  const { currentUser } = useSession()
  const [searchValue, setSearchValue] = useState('')
  const [inputFocus, setInputFocus] = useState(false)
  const debouncedSearchValue = useDebounce(searchValue, 500)

  const { organisations, loadingStates, error, pageInfo, onFetchMoreOrganisations, refetch } = useGetUserOrganisations({
    userId: Number(currentUser?.id),
    lazyQuery: true,
  })

  const onSearch = () => {
    refetch({
      filters: { searchTextFuzzy: debouncedSearchValue },
      organisationSwitcherScope: !!debouncedSearchValue,
    })
  }

  useEffect(() => {
    if (showModal && !currentUser?.isAdmin) {
      refetch()
    }
  }, [showModal])

  useEffect(() => {
    if (inputFocus) {
      onSearch()
    }
  }, [debouncedSearchValue])

  return (
    <>
      <Input
        data-testid="organisation-search-input"
        className="mb-3 w-full rounded-md border border-gray-200 p-3 text-sm focus:ring-primary"
        placeholder={t('general.search', 'Search')}
        aria-label={t('general.searchingForOrganisations', 'Searching for organisations')}
        onChange={(e) => setSearchValue(e)}
        onFocus={() => setInputFocus(true)}
      />
      <QueryResult loading={loadingStates.loading || loadingStates.setVariablesLoading} error={error}>
        {organisations.length ? (
          <InfiniteScroll
            dataLength={organisations.length}
            next={onFetchMoreOrganisations}
            hasMore={!!pageInfo?.hasNextPage}
            loader={<Spinner className="mt-5 h-14 md:w-16" />}
            scrollableTarget="InfiniteScroll"
          >
            <div className="border-b">
              {organisations.map((organisation) => (
                <SelectOrganisationUserListItem
                  organisation={organisation}
                  key={organisation.id}
                  onSelectOrganisation={onSelectOrganisation}
                  level={0}
                  selectedOrganisation={selectedOrganisation}
                  childrenCount={organisation.children?.totalCount || 0}
                />
              ))}
            </div>
          </InfiniteScroll>
        ) : (
          <p className="mt-10 text-center text-gray-500">
            {t('access.switchOrganisation.noOrganisations', 'There are no Organisations found.')}
          </p>
        )}
      </QueryResult>
    </>
  )
}
